// src/scene/atmosphere.ts
import * as THREE from 'three';
import gsap from 'gsap';
import type { Band } from './wallLayout';
import { glowTexture } from './glow';
import { focus } from './focus';

interface MoteSystem {
  name: string;
  points: THREE.Points;
  bounds: THREE.Box3;
  speeds: Float32Array;
  baseOpacity: number;
  /** Band centre Y — used to brighten motes near the focus pool (P2). */
  centerY: number;
  /** True while a pulseCluster() tween owns the opacity; skip focus modulation. */
  pulsing: boolean;
}

const SPREAD_X = 3.0;  // how far motes drift off the wall (toward the viewer)

export class Atmosphere {
  private systems: MoteSystem[] = [];

  constructor(scene: THREE.Scene, bands: Band[], width: number) {
    const glowMap = glowTexture();
    for (const band of bands) {
      const hot = band.name === 'wystawa';
      const count = hot ? 180 : 90;
      const yLen = Math.max(2, band.yEnd - band.yStart);
      const bounds = new THREE.Box3(
        new THREE.Vector3(0.2, band.yStart, 0),
        new THREE.Vector3(SPREAD_X, band.yEnd, width),
      );
      const positions = new Float32Array(count * 3);
      const speeds = new Float32Array(count);
      for (let i = 0; i < count; i++) {
        // deterministic-ish scatter from index (no reliance on Math.random for layout)
        positions[i * 3] = 0.2 + frac(i * 0.6180339) * SPREAD_X;
        positions[i * 3 + 1] = band.yStart + frac(i * 0.7548776) * yLen;
        positions[i * 3 + 2] = frac(i * 0.3098861) * width;
        speeds[i] = 0.12 + frac(i * 0.911) * 0.28;
      }
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      const baseOpacity = hot ? 0.85 : 0.45;
      const material = new THREE.PointsMaterial({
        color: band.color, size: hot ? 0.07 : 0.05, map: glowMap,
        alphaTest: 0.01, transparent: true, opacity: baseOpacity,
        blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true,
      });
      const points = new THREE.Points(geometry, material);
      points.raycast = () => {};
      scene.add(points);
      this.systems.push({
        name: band.name, points, bounds, speeds, baseOpacity,
        centerY: (band.yStart + band.yEnd) / 2, pulsing: false,
      });
    }
  }

  /** Brief opacity bump on the matching band's motes (~0.8s). Kept for HUD/select FX. */
  pulseCluster(name: string): void {
    const sys = this.systems.find((s) => s.name === name);
    if (!sys) return;
    const mat = sys.points.material as THREE.PointsMaterial;
    gsap.killTweensOf(mat);
    sys.pulsing = true;
    gsap.timeline({ onComplete: () => { sys.pulsing = false; } })
      .to(mat, { opacity: Math.min(1, sys.baseOpacity * 1.6), duration: 0.25, ease: 'power2.out' })
      .to(mat, { opacity: sys.baseOpacity, duration: 0.55, ease: 'power2.inOut' });
  }

  update(dt: number): void {
    dt = Math.min(dt, 0.05);
    const focusY = focus.getY();
    const span = focus.span;
    for (const s of this.systems) {
      const pos = s.points.geometry.getAttribute('position') as THREE.BufferAttribute;
      for (let i = 0; i < pos.count; i++) {
        let y = pos.getY(i) + s.speeds[i] * dt; // rise
        if (y > s.bounds.max.y) y = s.bounds.min.y; // respawn at floor
        pos.setY(i, y);
      }
      pos.needsUpdate = true;
      // P2: motes near the focus pool catch the light — lift opacity for the
      // band the viewer is in, sink the rest into the dark. Skipped while a
      // pulse tween owns the opacity so the two don't fight.
      if (!s.pulsing) {
        const d = Math.abs(s.centerY - focusY) / Math.max(span, 0.001);
        const near = 1 - Math.min(1, d);             // 1 at focus height → 0 away
        const mat = s.points.material as THREE.PointsMaterial;
        const target = s.baseOpacity * (0.45 + 0.85 * near);
        mat.opacity += (target - mat.opacity) * Math.min(1, dt * 4);
      }
    }
  }
}

function frac(n: number): number { return n - Math.floor(n); }
